import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { Action, on, createReducer } from '@ngrx/store';

import { RefTypeItem } from '@sd-portal/shared/ref-type-item';

import { auditLogAdapter } from '../shared-audit.adapters';
import { ExpenseSkeleton, ExpenseAuditLog } from '../../+models/expense.audit.model';
import * as actions from './expense.actions';

// States
interface ExpenseCategoryTypeState extends EntityState<RefTypeItem> {
  loading: boolean;
  loaded: boolean;
  error: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

interface ExpenseSkeletonState extends EntityState<ExpenseSkeleton> {
  loading: boolean;
  loaded: boolean;
  error: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

interface DeletedExpenseSkeletonState extends EntityState<ExpenseSkeleton> {
  loading: boolean;
  loaded: boolean;
  error: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

interface ExpenseAuditState extends EntityState<ExpenseAuditLog> {
  loading: boolean;
  loaded: boolean;
  error: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

// Adapters
const expenseCategoryTypeAdapter = createEntityAdapter<RefTypeItem>({
  selectId: p => p.id
});

const expenseSkeletonAdapter = createEntityAdapter<ExpenseSkeleton>({
  selectId: p => p.expenseId
});

const deletedExpenseSkeletonAdapter = createEntityAdapter<ExpenseSkeleton>({
  selectId: p => p.expenseId
});

// Entity selectors
export const expenseCategoryTypeSelectors = expenseCategoryTypeAdapter.getSelectors();
export const expenseSkeletonSelectors = expenseSkeletonAdapter.getSelectors();
export const deletedExpenseSkeletonSelectors = deletedExpenseSkeletonAdapter.getSelectors();

// Initial states
const initialPagedState = {
  loading: false,
  loaded: false,
  error: null,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
};

const expenseCategoryTypesInitialState = expenseCategoryTypeAdapter.getInitialState({
  loading: false,
  loaded: false,
  error: null
});

const expenseSkeletonInitialState = expenseSkeletonAdapter.getInitialState({
  loading: false,
  loaded: false,
  error: null
});

const deletedExpenseSkeletonInitialState = deletedExpenseSkeletonAdapter.getInitialState({
  loading: false,
  loaded: false,
  error: null
});

const expenseAuditInitialState = auditLogAdapter.getInitialState(initialPagedState);

export interface State {
  expenseCategoryTypes: ExpenseCategoryTypeState;
  expenseSkeleton: ExpenseSkeletonState;
  deletedExpenseSkeletons: DeletedExpenseSkeletonState;
  expenseAudit: ExpenseAuditState;
}

const initialState: State = {
  expenseCategoryTypes: expenseCategoryTypesInitialState,
  expenseSkeleton: expenseSkeletonInitialState,
  deletedExpenseSkeletons: deletedExpenseSkeletonInitialState,
  expenseAudit: expenseAuditInitialState
};

const expenseReducer = createReducer<State>(
  initialState,
  on(actions.ResetExpense, () => initialState),

  on(actions.LoadExpenseCategoryTypes, state => ({
    ...state,
    expenseCategoryTypes: {
      ...state.expenseCategoryTypes,
      loading: true,
      loaded: false,
      error: null
    }
  })),

  on(actions.ExpenseCategoryTypesLoaded, (state, action) => ({
    ...state,
    expenseCategoryTypes: {
      ...state.expenseCategoryTypes,
      ...expenseCategoryTypeAdapter.setAll(action.payload, state.expenseCategoryTypes),
      loading: false,
      loaded: true,
      error: null
    }
  })),

  on(actions.LoadExpenseSkeleton, state => ({
    ...state,
    expenseSkeleton: {
      ...state.expenseSkeleton,
      loading: true,
      loaded: false,
      error: null
    }
  })),

  on(actions.LoadDeletedExpenseSkeletons, state => ({
    ...state,
    deletedExpenseSkeletons: {
      ...state.deletedExpenseSkeletons,
      loading: true,
      loaded: false,
      error: null
    }
  })),

  on(actions.ExpenseSkeletonLoaded, (state, action) => ({
    ...state,
    expenseSkeleton: {
      ...state.expenseSkeleton,
      ...expenseSkeletonAdapter.addOne(action.payload, state.expenseSkeleton),
      loading: false,
      loaded: true,
      error: null
    }
  })),

  on(actions.DeletedExpenseSkeletonsLoaded, (state, action) => ({
    ...state,
    deletedExpenseSkeletons: {
      ...state.deletedExpenseSkeletons,
      ...expenseSkeletonAdapter.setAll(action.payload, state.deletedExpenseSkeletons),
      loading: false,
      loaded: true,
      error: null
    }
  })),

  on(actions.LoadExpenseAudit, (state, action) => ({
    ...state,
    expenseAudit: {
      ...state.expenseAudit,
      loading: true,
      loaded: false,
      error: null,
      lastAuditLogId:
        action.expenseId === state.expenseAudit.selectedId
          ? state.expenseAudit.lastAuditLogId
          : null
    }
  })),

  on(actions.ExpenseAuditLoaded, (state, action) => ({
    ...state,
    expenseAudit: {
      ...state.expenseAudit,
      ...(!state.expenseAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.expenseAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.expenseAudit)),
      loading: false,
      loaded: true,
      error: null,
      selectedId: action.expenseId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return expenseReducer(state, action);
}
